import { Children, forwardRef, HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  icon?: keyof typeof IconType;
  iconPosition?: "left" | "right";
  iconSize?: number;
  iconViewBox?: string;
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>((badgeProps, ref) => {
  const { icon, iconSize, iconPosition, iconViewBox, ...divProps } = badgeProps;
  const hasChildren = Children.toArray(badgeProps.children).length > 0;

  return (
    <div
      ref={ref}
      {...divProps}
      className={twMerge(
        "inline-flex items-center self-start rounded-3xl bg-gray-100 text-sm leading-none font-bold",
        hasChildren ? "gap-2 px-2 py-1" : "p-1",
        badgeProps.className,
      )}
    >
      {icon && iconPosition !== "right" && (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
          <Icon size={iconSize ?? 0.7} icon={icon} viewBox={iconViewBox} />
        </span>
      )}
      {hasChildren && (
        <span className="inline-flex h-5 items-center">
          {badgeProps.children}
        </span>
      )}
      {icon && iconPosition === "right" && (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
          <Icon size={iconSize ?? 0.7} icon={icon} viewBox={iconViewBox} />
        </span>
      )}
    </div>
  );
});

const Green = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-emerald-100 text-emerald-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Blue = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-blue-100 text-blue-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Black = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-zinc-800 text-white", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const White = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge(
        "text-grey-700 border-grey-700 border bg-white",
        props.className,
      )}
    >
      {props.children}
    </Badge>
  );
};

const WhiteFill = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge(
        "border border-zinc-300 bg-white text-zinc-300",
        props.className,
      )}
    >
      {props.children}
    </Badge>
  );
};

const Grey = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-gray-500 text-white", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Yellow = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-yellow-100 text-yellow-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Lime = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-lime-100 text-lime-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Red = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-red-100 text-red-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};
const Indigo = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-indigo-100 text-indigo-800", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Zinc = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-zinc-100 text-zinc-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Orange = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-orange-100 text-orange-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Cyan = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-cyan-100 text-cyan-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Purple = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-purple-100 text-purple-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

const Amber = (props: BadgeProps) => {
  return (
    <Badge
      {...props}
      className={twMerge("bg-amber-100 text-amber-700", props.className)}
    >
      {props.children}
    </Badge>
  );
};

export {
  Badge,
  Green as BadgeGreen,
  White as BadgeWhite,
  WhiteFill as BadgeWhiteFill,
  Grey as BadgeGrey,
  Blue as BadgeBlue,
  Yellow as BadgeYellow,
  Lime as BadgeLime,
  Red as BadgeRed,
  Indigo as BadgeIndigo,
  Zinc as BadgeZinc,
  Orange as BadgeOrange,
  Cyan as BadgeCyan,
  Purple as BadgePurple,
  Amber as BadgeAmber,
  Black as BadgeBlack,
};

Badge.displayName = "Badge";
